import dayjs from "dayjs";

const TICKET_DESC_STORAGE_KEY = "vendor_ticket_descriptions";

export const TICKET_STATE = {
  APERTO: "aperto",
  IN_LAVORAZIONE: "in-lavorazione",
  CHIUSO: "chiuso",
};

export function normalizeTicketTimestamp(raw) {
  if (raw == null) return null;
  if (typeof raw === "string") return raw;
  if (Array.isArray(raw) && raw.length >= 3) {
    const [y, mo, d, h = 0, mi = 0, s = 0] = raw;
    return dayjs(new Date(y, mo - 1, d, h, mi, s)).toISOString();
  }
  return null;
}

export function formatTicketDate(raw) {
  const normalized = normalizeTicketTimestamp(raw);
  if (!normalized) return "";
  return dayjs(normalized).format("DD/MM/YYYY HH:mm");
}

export function getTicketStateLabel(state) {
  switch (state) {
    case TICKET_STATE.APERTO:
      return "Aperto";
    case TICKET_STATE.IN_LAVORAZIONE:
      return "In lavorazione";
    case TICKET_STATE.CHIUSO:
      return "Chiuso";
    default:
      return state ?? "";
  }
}

export function getTicketStateColor(state) {
  switch (state) {
    case TICKET_STATE.APERTO:
      return "#e00036";
    case TICKET_STATE.IN_LAVORAZIONE:
      return "#995C00";
    case TICKET_STATE.CHIUSO:
      return "#2e7d32";
    default:
      return "#757575";
  }
}

export function hasOpenTickets(tickets) {
  if (!Array.isArray(tickets) || tickets.length === 0) return false;
  return tickets.some((t) => t.state !== TICKET_STATE.CHIUSO);
}

export function shouldExpandNewTicket(tickets) {
  return !hasOpenTickets(tickets);
}

export function isChatWritable(state) {
  return state === TICKET_STATE.IN_LAVORAZIONE;
}

function toMillis(raw) {
  const normalized = normalizeTicketTimestamp(raw);
  return normalized ? new Date(normalized).getTime() : 0;
}

export function sortChatMessagesAscending(items) {
  return [...items].sort((a, b) => toMillis(a.timestamp) - toMillis(b.timestamp));
}

export function mergeChatMessages(existing, incoming) {
  const byId = new Map(existing.map((item) => [item.id, item]));
  for (const item of incoming) {
    const prev = byId.get(item.id);
    byId.set(item.id, prev ? { ...prev, ...item } : item);
  }
  return sortChatMessagesAscending([...byId.values()]);
}

export function normalizePollAfterTimestamp(raw) {
  const normalized = normalizeTicketTimestamp(raw);
  if (!normalized) return undefined;
  const dt = new Date(normalized);
  if (Number.isNaN(dt.getTime())) return undefined;
  const pad = (value) => String(value).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
}

export function getPollAfterTimestamp(messages) {
  return normalizePollAfterTimestamp(getLatestChatTimestamp(messages));
}

export function collectOwnUnreadMessageIds(messages, isOwnMessage) {
  if (!Array.isArray(messages) || messages.length === 0) return [];
  return messages
    .filter((msg) => !msg.isDescription && isOwnMessage(msg) && !msg.read)
    .map((msg) => msg.id);
}

export function applyReadMessageIds(messages, readMessageIds) {
  if (!readMessageIds?.length) return messages;
  const ids = new Set(readMessageIds);
  let changed = false;
  const next = messages.map((msg) => {
    if (!ids.has(msg.id) || msg.read) return msg;
    changed = true;
    return { ...msg, read: true };
  });
  return changed ? next : messages;
}

export function applyPollResult(messages, { items = [], readMessageIds = [] } = {}) {
  let next = messages;
  if (items.length) {
    next = mergeChatMessages(next, items);
  }
  return applyReadMessageIds(next, readMessageIds);
}

export function mergeTicketSnapshots(existingTickets, snapshots) {
  if (!snapshots?.length || !existingTickets?.length) return existingTickets;
  const byId = new Map(snapshots.map((snapshot) => [snapshot.id, snapshot]));
  return existingTickets.map((ticket) => {
    const snapshot = byId.get(ticket.id);
    if (!snapshot) return ticket;
    return {
      ...ticket,
      state: snapshot.state ?? ticket.state,
      updatedAt: snapshot.updatedAt ?? ticket.updatedAt,
      unreadCount: snapshot.unreadCount != null ? Number(snapshot.unreadCount) : ticket.unreadCount,
    };
  });
}

export function getLatestChatTimestamp(messages) {
  if (!Array.isArray(messages) || messages.length === 0) {
    return null;
  }
  let latest = null;
  let latestMs = 0;
  for (const msg of messages) {
    const ms = toMillis(msg.timestamp);
    if (ms >= latestMs) {
      latestMs = ms;
      latest = msg.timestamp;
    }
  }
  return latest;
}

export function buildSentChatMessage({ messageId, message, attachmentName }) {
  return {
    id: messageId,
    operator: false,
    message: message?.trim() || null,
    attachmentName: attachmentName ?? null,
    timestamp: new Date().toISOString(),
    editable: true,
    read: false,
  };
}

export function cacheTicketDescription(ticketId, description) {
  if (!ticketId || !description) return;
  try {
    const map = JSON.parse(globalThis.sessionStorage.getItem(TICKET_DESC_STORAGE_KEY) || "{}");
    map[ticketId] = description;
    globalThis.sessionStorage.setItem(TICKET_DESC_STORAGE_KEY, JSON.stringify(map));
  } catch {
    // ignore storage errors
  }
}

export function getCachedTicketDescription(ticketId) {
  if (!ticketId) return "";
  try {
    const map = JSON.parse(globalThis.sessionStorage.getItem(TICKET_DESC_STORAGE_KEY) || "{}");
    return typeof map[ticketId] === "string" ? map[ticketId] : "";
  } catch {
    return "";
  }
}

export function buildDescriptionMessage(ticketId, createdAt, descriptionOverride) {
  const description = (descriptionOverride ?? getCachedTicketDescription(ticketId))?.trim();
  if (!description) return null;
  return {
    id: `desc-${ticketId}`,
    operator: false,
    message: description,
    attachmentName: null,
    timestamp: createdAt,
    editable: false,
    isDescription: true,
    read: true,
  };
}

export function mapTicketListItem(raw) {
  return {
    id: raw.id,
    subject: raw.subject,
    state: raw.state,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    unreadCount: raw.unreadCount != null ? Number(raw.unreadCount) : 0,
    description: raw.description?.trim() || getCachedTicketDescription(raw.id) || "",
  };
}
